import { Cause, Effect, Exit, Fiber, Layer, Match, Ref, Schema, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { AcceptedWork } from "../accepted-work.ts";
import { QueryWork } from "../query-work.ts";
import { makeSessionOwner } from "../session-owner.ts";
import { StationApi } from "../../api/station/service.ts";
import type { StationClientFailure } from "../../api/station/model.ts";
import { workspaceInvalid } from "@workerstation/shared/errors/workspace-http";
import { WorkspaceDraft } from "./persistence-model.ts";
import { StationPersistence } from "./persistence-service.ts";
import { StationSession } from "./session-service.ts";
import { PendingSaveSessions } from "./pending-service.ts";
import { makeWorkbenchQueries } from "./workbench-queries.ts";
import { initialStationWorkbench } from "./workbench-model.ts";
import { StationBrowserIo, StationWorkbenchSessions, type StationWorkbenchSession, type StationWorkbenchAction } from "./workbench-service.ts";
import { makeWorkbenchCore } from "./workbench-core.ts";
import { bootstrapWorkbench } from "./workbench-bootstrap.ts";
import { openWorkbenchWorkspace, reloadWorkbenchWorkspace } from "./workbench-navigation.ts";
import { addWorkbenchWorkspace, deleteWorkbenchWorkspace, reorderWorkbenchWorkspaces } from "./workbench-mutations.ts";

export const StationWorkbenchSessionsLive = (root: Scope.Scope) => Layer.effect(StationWorkbenchSessions, Effect.gen(function* () {
  const api = yield* StationApi, io = yield* StationBrowserIo, persistence = yield* StationPersistence, station = yield* StationSession;
  const accepted = yield* AcceptedWork, pendingSessions = yield* PendingSaveSessions, queryWork = yield* QueryWork, registry = yield* AtomRegistry.AtomRegistry;
  const sessions = yield* makeSessionOwner(root), failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  const retain = (result: Exit.Exit<unknown, unknown>) => Exit.isFailure(result) ? Ref.update(failures, current => {
    const next = [...current];
    for (const reason of result.cause.reasons) if (reason._tag === "Die" && !next.includes(reason)) next.push(reason);
    return next;
  }) : Effect.void;
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const make = Effect.gen(function* () {
    const owner = yield* Scope.Scope, state = yield* Ref.make(initialStationWorkbench(yield* io.blank)), edits = yield* Ref.make(0);
    const read = Ref.get(state), snapshot = Atom.make(read);
    const publish = Effect.gen(function* () {
      if (root.state._tag === "Closed" || owner.state._tag === "Closed" || (yield* read).closed) return;
      const result = yield* Effect.exit(Effect.sync(() => registry.refresh(snapshot))); yield* retain(result); return yield* result;
    });
    const pending = yield* pendingSessions.open(value => Ref.update(state, current => current.closed ? current : { ...current, phase: value.phase }).pipe(Effect.andThen(publish)));
    const queries = yield* makeWorkbenchQueries(root, owner, failures).pipe(Effect.provideService(QueryWork, queryWork));
    yield* Effect.addFinalizer(() => Ref.update(state, current => ({ ...current, closed: true })));
    const core = makeWorkbenchCore({ api, io, persistence, station, accepted }, root, owner, state, pending, queries, publish, retain);
    const saveEdit = Effect.fn("Frontend.StationWorkbench.saveEdit")(function* (action: Extract<StationWorkbenchAction, { readonly _tag: "SaveEdit" }>) {
      const current = yield* read;
      if (!current.booted || current.workspaceId !== action.workspaceId || !(yield* core.active())) return;
      const target = yield* core.target(action.workspaceId);
      const draft = yield* Schema.decodeUnknownEffect(WorkspaceDraft)(action.draft).pipe(Effect.mapError(() => workspaceInvalid("Invalid editor draft.")));
      yield* Ref.update(edits, value => value + 1);
      yield* core.update(value => ({ ...value, draft })); yield* pending.schedule(target, draft);
    });
    const own = (operation: Effect.Effect<void, StationClientFailure>, startup = false) => Effect.gen(function* () {
      if (!(yield* core.active())) return;
      const child = yield* Effect.forkIn(operation, owner, { uninterruptible: false });
      const result = yield* Effect.exit(Fiber.join(child)); yield* retain(result);
      if (Exit.isFailure(result) && (yield* core.active())) yield* core.update(current => startup
        ? { ...current, booted: true, bootstrapError: "Could not load the workerstation session. Reload to try again." }
        : { ...current, phase: "error" });
    });
    const openWorkspace = (id: string) => own(openWorkbenchWorkspace(core, id));
    const dispatch = (action: StationWorkbenchAction) => own(Match.value(action).pipe(
      Match.tag("Open", value => openWorkbenchWorkspace(core, value.workspaceId)),
      Match.tag("Add", () => addWorkbenchWorkspace(core)),
      Match.tag("Delete", value => deleteWorkbenchWorkspace(core, value.workspaceId)),
      Match.tag("Reorder", value => reorderWorkbenchWorkspaces(core, value.movedId, value.targetId, value.placeAfterTarget)),
      Match.tag("Reload", () => reloadWorkbenchWorkspace(core, edits)),
      Match.tag("SaveEdit", saveEdit), Match.exhaustive
    ));
    const started = yield* Ref.make(false);
    const start = Effect.gen(function* () {
      if (yield* Ref.getAndSet(started, true)) return;
      yield* own(bootstrapWorkbench(core), true);
    });
    return { snapshot, read, start, dispatch, openWorkspace, invalidateReload: Ref.update(edits, value => value + 1), flush: pending.flush } satisfies StationWorkbenchSession;
  });
  return StationWorkbenchSessions.of({ open: sessions.open(make) });
}));
